import { useEffect, useState } from 'react';
import { Bookmark, X } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const STORAGE_KEY = 'jw_bookmark_flyer_dismissed_v1';

/**
 * A one-time flyer that invites the user to bookmark Jwalek.
 * Appears ~4s after first mount, once per browser (localStorage flag),
 * and never re-appears after dismissal or bookmarking action.
 * Skips known non-user routes (auth callback, admin).
 */
const BookmarkFlyer = () => {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const { t, lang } = useLanguage();

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) === '1') return;
    } catch (err) {
      // localStorage may be unavailable (privacy mode / SSR); safest is to not show the flyer.
      console.warn('BookmarkFlyer: localStorage read failed, skipping', err);
      return;
    }
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (path.startsWith('/admin') || path.startsWith('/auth')) return;
    const t = setTimeout(() => setVisible(true), 4000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setLeaving(true);
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch (err) {
      // Non-fatal — user just won't have their preference persisted across reloads.
      console.warn('BookmarkFlyer: localStorage write failed', err);
    }
    setTimeout(() => setVisible(false), 260);
  };

  if (!visible) return null;

  // Detect platform for shortcut hint
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  const shortcut = isMac ? '⌘ + D' : 'Ctrl + D';

  return (
    <div
      role="dialog"
      aria-label="Bookmark Jwalek"
      data-testid="bookmark-flyer"
      className={`fixed ${lang === 'ar' ? 'left-4' : 'right-4'} z-40 max-w-sm w-[calc(100%-2rem)] sm:w-[360px] transition-all duration-300 bottom-20 md:bottom-4 ${leaving ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}
    >
      <div className="relative overflow-hidden rounded-2xl border border-white/15 backdrop-blur-xl shadow-2xl"
        style={{ background: 'linear-gradient(140deg, rgba(230,36,138,0.95) 0%, rgba(255,107,53,0.95) 55%, rgba(255,165,0,0.95) 100%)' }}
      >
        <button
          onClick={dismiss}
          data-testid="bookmark-flyer-close-button"
          aria-label={t('common.close', 'Close')}
          className={`absolute top-2 ${lang === 'ar' ? 'left-2' : 'right-2'} w-7 h-7 grid place-items-center rounded-full bg-black/15 text-white hover:bg-black/25 transition`}
        >
          <X className="w-4 h-4" />
        </button>
        <div className={`p-4 sm:p-5 ${lang === 'ar' ? 'pl-10' : 'pr-10'}`}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 grid place-items-center shrink-0">
              <Bookmark className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-display text-base sm:text-lg font-bold leading-tight">{t('bookmark.title', 'Save Jwalek for later')}</p>
              <p className="text-white/90 text-sm mt-1 leading-snug">{t('bookmark.body', 'Bookmark us so your designs, orders, and community feed are one tap away.')}</p>
              <div className="mt-3 flex items-center gap-2">
                <kbd className="px-2 py-1 rounded-md bg-white/25 text-white text-[11px] font-mono font-semibold border border-white/30">{shortcut}</kbd>
                <span className="text-white/90 text-xs">{t('bookmark.toBookmark', 'to bookmark')}</span>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={dismiss}
                  data-testid="bookmark-flyer-got-it-button"
                  className="px-3 py-1.5 rounded-full bg-white text-[#0A0E27] text-xs font-semibold hover:bg-white/90 transition"
                >
                  {t('bookmark.gotIt', 'Got it')}
                </button>
                <button
                  onClick={dismiss}
                  data-testid="bookmark-flyer-not-now-button"
                  className="px-3 py-1.5 rounded-full bg-white/15 text-white text-xs font-medium hover:bg-white/25 transition border border-white/20"
                >
                  {t('bookmark.notNow', 'Not now')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookmarkFlyer;
